import { computeVerifier } from '../crypto/hmac';
import { bytesToBase64Url, base64UrlToBytes, randomBytes } from '../crypto/encoding';
import { deriveKeysFromPassword, PBKDF2_ITERATIONS, type DerivedKeys } from '../crypto/keys';
import { CruzFindVault, type VaultMetaRow } from '../db/schema';
import { TaxpayerRepo, TransactionRepo } from '../db/repo';
import { SubscriberProfileRepo } from '../db/subscriberProfileRepo';
import { SalesLedgerRepo } from '../db/salesLedgerRepo';
import { ExpenseLedgerRepo } from '../db/expenseLedgerRepo';

export type SessionState =
  | { kind: 'locked' }
  | {
      kind: 'unlocked';
      keys: DerivedKeys;
      taxpayers: TaxpayerRepo;
      transactions: TransactionRepo;
      subscriberProfile: SubscriberProfileRepo;
      salesLedger: SalesLedgerRepo;
      expenseLedger: ExpenseLedgerRepo;
    };

export class WrongPasswordError extends Error {
  constructor() {
    super('Wrong password');
    this.name = 'WrongPasswordError';
  }
}

export class VaultNotInitializedError extends Error {
  constructor() {
    super('Vault has not been initialized; call initialize() before unlock()');
    this.name = 'VaultNotInitializedError';
  }
}

export class VaultAlreadyInitializedError extends Error {
  constructor() {
    super('Vault is already initialized');
    this.name = 'VaultAlreadyInitializedError';
  }
}

export class SandboxSession {
  private state: SessionState = { kind: 'locked' };

  constructor(private readonly db: CruzFindVault = new CruzFindVault()) {}

  getState(): SessionState {
    return this.state;
  }

  async isInitialized(): Promise<boolean> {
    const row = await this.db.vaultMeta.get('singleton');
    return row !== undefined;
  }

  async initialize(password: string): Promise<void> {
    if (await this.isInitialized()) throw new VaultAlreadyInitializedError();

    const salt = randomBytes(16);
    const keys = await deriveKeysFromPassword(password, salt, PBKDF2_ITERATIONS);
    const verifier = await computeVerifier(keys.verifierKey);

    const row: VaultMetaRow = {
      id: 'singleton',
      kdfAlgorithm: 'PBKDF2-SHA256',
      kdfSalt: bytesToBase64Url(salt),
      kdfIterations: PBKDF2_ITERATIONS,
      verifier,
      createdAt: Date.now(),
    };
    await this.db.vaultMeta.put(row);

    this.state = {
      kind: 'unlocked',
      keys,
      taxpayers: new TaxpayerRepo(this.db, keys.aesKey, keys.hmacKey),
      transactions: new TransactionRepo(this.db, keys.aesKey),
      subscriberProfile: new SubscriberProfileRepo(this.db, keys.aesKey),
      salesLedger: new SalesLedgerRepo(this.db, keys.aesKey),
      expenseLedger: new ExpenseLedgerRepo(this.db, keys.aesKey),
    };
  }

  async unlock(password: string): Promise<void> {
    const meta = await this.db.vaultMeta.get('singleton');
    if (!meta) throw new VaultNotInitializedError();

    const keys = await deriveKeysFromPassword(
      password,
      base64UrlToBytes(meta.kdfSalt),
      meta.kdfIterations,
    );
    const computed = await computeVerifier(keys.verifierKey);
    if (computed !== meta.verifier) throw new WrongPasswordError();

    this.state = {
      kind: 'unlocked',
      keys,
      taxpayers: new TaxpayerRepo(this.db, keys.aesKey, keys.hmacKey),
      transactions: new TransactionRepo(this.db, keys.aesKey),
      subscriberProfile: new SubscriberProfileRepo(this.db, keys.aesKey),
      salesLedger: new SalesLedgerRepo(this.db, keys.aesKey),
      expenseLedger: new ExpenseLedgerRepo(this.db, keys.aesKey),
    };
  }

  lock(): void {
    // CryptoKey handles are non-extractable; dropping the reference is the strongest
    // guarantee available — the underlying material is never exposed to JS heap.
    this.state = { kind: 'locked' };
  }

  /** Hard-reset the local vault. Destroys all on-device taxpayer data. Test-only / explicit factory-reset flows. */
  async destroy(): Promise<void> {
    this.lock();
    await this.db.delete();
  }
}
